import find from 'lodash/find';
import get from 'lodash/get';
import map from 'lodash/map';
import { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import agentProfilesResource from 'src/actions/agent/agentProfilesResource';
import { AgentUser } from 'src/presentation/components/pages/people/users/types/AgentUser';
import agentUsersResource from 'src/actions/agentUsers/agentUsersResource';
import { CsvUploadStatus } from 'src/models/Users/CsvUpload';
import { DomainError } from 'src/models/DomainError';
import { getAllAgents } from 'src/actions/agentUsers/helpers';
import { getUserStatus } from 'src/presentation/components/pages/people/users/helpers/usersHelper';
import { RootState } from 'src/actions/configuration/rootReducer';
import RoutingGroup from 'src/models/RoutingGroup/RoutingGroup';
import routingGroupsResource from 'src/actions/routingGroups/routingGroupsResource';
import teamsResource, { Team } from 'src/actions/teams/teamsResource';
import { toastError } from 'src/actions/toast';
import { User } from 'src/models/Users/User';
import useResource, {
  allSucceeded,
  someFailed,
  STATUS_FAILED,
  STATUS_LOADING,
  STATUS_SUCCEEDED,
  useResourceValues,
} from 'src/presentation/hooks/useResource';
import reportBuilderConfigResource from 'src/actions/reportBuilderConfig/reportBuilderConfigResource';
import useDocumentResource from 'src/presentation/hooks/useDocumentResource';
import usersResource from 'src/actions/users/usersResource';
import useSkills from 'src/presentation/hooks/useSkills';
import useRoles from 'src/presentation/components/pages/people/hooks/useRoles';
import { Role } from 'src/actions/roles/rolesResource';

type ResultType = {
  errors: DomainError[];
  loadingState: string;
  resetErrors: () => void;
  agentUsers?: AgentUser[];
};

type UserTeams = Record<string, Team[]>;
type UserRoutingGroups = Record<string, RoutingGroup[]>;

export default function useAgentUsers(): ResultType {
  const dispatch = useDispatch();

  const { values: users, loadingState: usersLoadingState } = useResource(usersResource);
  const { errors } = useResourceValues(agentUsersResource);
  const { values: agents, loadingState: agentsLoadingState } = useResource(agentProfilesResource);
  const { values: routingGroups, loadingState: routingGroupsState } = useResource<RoutingGroup>(routingGroupsResource);
  const { values: teams, loadingState: teamsLoadingState } = useResource(teamsResource);
  const { usersCsvUploadStatus } = useResourceValues(agentUsersResource);

  const [loadingState, setLoadingState] = useState(STATUS_LOADING);
  const [agentUsers, setAgentUsers] = useState<AgentUser[]>();

  const currentAgentId = useSelector((state: RootState) => state.auth.userId);

  useDocumentResource(reportBuilderConfigResource);

  const userTeams = useMemo<UserTeams>(() => getAllAgents<Team>(teams), [teams]);
  const userRoutingGroups = useMemo<UserRoutingGroups>(
    () => getAllAgents<RoutingGroup>(routingGroups),
    [routingGroups]
  );

  const { roles: allRoles } = useRoles();
  const { dataMergeFinished } = useSkills();
  const skillsLoadingState = dataMergeFinished ? STATUS_SUCCEEDED : STATUS_LOADING;

  useEffect(() => {
    if (usersCsvUploadStatus === CsvUploadStatus.Started) {
      return;
    }

    if (someFailed(usersLoadingState, agentsLoadingState, teamsLoadingState, routingGroupsState)) {
      setLoadingState(STATUS_FAILED);
      dispatch(toastError('Error while loading data. Please wait and try refreshing the page'));
      return;
    }

    if (
      !allSucceeded(usersLoadingState, agentsLoadingState, teamsLoadingState, routingGroupsState, skillsLoadingState)
    ) {
      return;
    }

    const agentUsers = getUsersWithProfiles({
      users,
      agents,
      userTeams,
      userRoutingGroups,
      currentAgentId,
      allRoles,
    });

    setAgentUsers(agentUsers);
    setLoadingState(STATUS_SUCCEEDED);
  }, [
    agents,
    agentsLoadingState,
    currentAgentId,
    dispatch,
    routingGroups,
    routingGroupsState,
    setAgentUsers,
    skillsLoadingState,
    teamsLoadingState,
    userRoutingGroups,
    users,
    usersCsvUploadStatus,
    usersLoadingState,
    userTeams,
    allRoles,
  ]);

  return {
    loadingState,
    errors,
    resetErrors: usersResource.actions.resetErrors as any,
    agentUsers,
  };
}

const getUsersWithProfiles = ({ users, agents, userTeams, userRoutingGroups, currentAgentId, allRoles }) => {
  for (const agent of agents) {
    const matchedUser: User = find(users, user => agent.id === user.id);
    if (!matchedUser) {
      continue;
    }

    const roles = get(matchedUser, 'roleIds', [])
      .map((id: string) => allRoles.find((role: Role) => role.id === id))
      .filter((role: Role | undefined) => role && !role.disabledAt)
      .map((role: Role) => role.name);

    const teamCount = get(userTeams, `${matchedUser.id}`, []).length;
    const teamIds = map(userTeams[matchedUser.id], ({ id }) => id);
    const routingGroupIds = map(userRoutingGroups[matchedUser.id], ({ id }) => id);
    const inboxesCount = routingGroupIds.length;
    const isCurrentUser = currentAgentId === matchedUser.id;

    Object.assign(agent, {
      ...matchedUser,
      teamIds,
      teams: teamCount,
      agentRoles: roles,
      inboxes: inboxesCount,
      routingGroupIds,
      displayName: `${agent.name}${isCurrentUser ? ' (You)' : ''}`,
      status: getUserStatus(matchedUser),
    });
  }

  return agents;
};
